/**
 * Customers data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. Owned by the customers feature. Shared entity reads (jobs, techs, customers,
 * vehicles, catalog) live in core/entities.ts.
 *
 * Endpoints (owners): customers / addresses / vehicles / consents / merge / erasure = P1 customers module
 * (ops.customers, ops.customer_addresses, ops.vehicles, ops.contact_consents, DEL-1 retention); the message thread = P4.
 */
import { APP, type Http } from '@app/core/http';
import { isMissingRoute, orRollingOut } from '@app/core/live/dto';
import type { Address, Customer, ISO, Message, UUID, Vehicle } from '@app/core/types';

/** `ops.contact_consents` row (kept through erasure: a STOP must never be forgotten). */
export interface ConsentRow { id: UUID; channel: 'sms' | 'email' | 'voice'; address: string; consent: boolean; source: string; text: string | null; at: ISO }

export interface CustomerInput {
  kind: Customer['kind']; first_name: string | null; last_name: string | null; company: string | null;
  phone_e164: string | null; email: string | null; tags: string[]; notes: string | null;
}
export interface AddressInput { label: string | null; line1: string; line2: string | null; city: string; state: string; zip: string; access_notes: string | null; is_default: boolean }
export interface VehicleInput {
  vin: string | null; year: number | null; make: string | null; model: string | null; trim: string | null; fuel: Vehicle['fuel'];
  plate: string | null; plate_state: string | null; color: string | null; unit_no: string | null; odometer_mi: number | null;
}
export interface MergeResult {
  survivor_id: UUID; merged_id: UUID;
  moved: { jobs: number; vehicles: number; addresses: number; messages: number };
  sms_consent: Customer['sms_consent'];
}
export interface ErasureResult { customer_id: UUID; status: 'queued' | 'done'; erased_at: ISO | null }

export interface Api {
  update(id: string, patch: Partial<CustomerInput>, version: number | undefined): Promise<Customer>;
  addAddress(customerId: string, body: AddressInput, key: string): Promise<Address>;
  updateAddress(customerId: string, addressId: string, patch: Partial<AddressInput>): Promise<Address>;
  addVehicle(customerId: string, body: VehicleInput, key: string): Promise<Vehicle>;
  updateVehicle(vehicleId: string, patch: Partial<VehicleInput>, version: number | undefined): Promise<Vehicle>;
  consents(customerId: string): Promise<ConsentRow[]>;
  messages(customerId: string): Promise<Message[]>;
  merge(body: { survivor_id: string; merged_id: string }, key: string): Promise<MergeResult>;
  /** P1 retention (DEL-1): PII removed, jobs/payments kept anonymized, consent/STOP records kept. */
  erase(customerId: string, confirmName: string, key: string): Promise<ErasureResult>;
}

const C = (id: string) => `${APP}/customers/${encodeURIComponent(id)}`;
const items = <T,>(r: { items: T[] } | T[]) => (Array.isArray(r) ? r : r.items);

export const live = (http: Http): Api => ({
  update: (id, patch, version) => http.patch(C(id), patch, { version }),
  addAddress: (cid, body, key) => http.post(`${C(cid)}/addresses`, body, { idempotencyKey: key }),
  updateAddress: (cid, aid, patch) => http.patch(`${C(cid)}/addresses/${encodeURIComponent(aid)}`, patch),
  addVehicle: (cid, body, key) => http.post(`${C(cid)}/vehicles`, body, { idempotencyKey: key }),
  updateVehicle: (vid, patch, version) => http.patch(`${APP}/vehicles/${encodeURIComponent(vid)}`, patch, { version }),
  consents: (cid) => http.get<{ items: ConsentRow[] } | ConsentRow[]>(`${C(cid)}/consents`).then(items),
  // No staff conversation / merge route yet (P4 / P1): the thread reads empty, merge is "Rolling out".
  messages: (cid) => http.get<{ items: Message[] } | Message[]>(`${APP}/conversations/${encodeURIComponent(cid)}/messages`, { query: { limit: 20 } }).then(items)
    .catch((e) => { if (isMissingRoute(e)) return []; throw e; }),
  merge: (body, key) => http.post<MergeResult>(`${APP}/customers/merge`, body, { idempotencyKey: key }).catch(orRollingOut('customer_merge')),
  erase: (cid, confirmName, key) => http.post(`${C(cid)}/erase`, { confirm_name: confirmName }, { idempotencyKey: key }),
});
